import React, { useState, useEffect } from 'react';
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import volumeAndWip from "../../assets/images/volumeAndWip.svg";

import {
  GetTargetAndWIPVolumeResponse,
  GetTargetAndWIPVolumeRequest,
  buildGetTargetAndWIPVolumeRequest, VerifyVolumeResponse
} from './model';
import { useAppDispatch, useAppSelector } from '../../store';
import { clearAllProcessPathOverrides } from '../../store/slices/volumesAndWipSlice';
import { showNotification, showError } from '../../store/slices/errorSlice';
import Footer from "../../components/Footer";
import './index.scss';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/constants';
import RatesRosterLoader from "../../components/RatesRosterLoader/RatesRosterLoader";
import VolumeTargetDisplay from "./components/VolumeTargetDisplay/VolumeTargetDisplayContainer";
import ProcessPathVolumeRow from "./components/ProcessPathVolumeRow/ProcessPathVolumeRowContainer";
import { PlanStatus } from "../../utils/constants";

interface VolumesAndWipOwnProps {
  readOnly?: boolean;
}

interface VolumesAndWipStateProps {
  targetAndWIPVolume?: GetTargetAndWIPVolumeResponse;
  planId: string | null;
  date: string | null;
  shiftType: string | null;
  stationCode: string | null;
}

interface VolumesAndWipDispatchProps {
  getTargetAndWipVolume: (getTargetAndWIPVolumeRequest: GetTargetAndWIPVolumeRequest) => void;
  verifyVolumes: (planId: string, processPathOverrides?: any) => Promise<any>;
  resetShiftPlanOutput: () => void;
}

type Props = VolumesAndWipOwnProps & VolumesAndWipStateProps & VolumesAndWipDispatchProps;

/**
 * Volumes and WIP component
 */
const VolumesAndWip: React.FC<Props> = ({
  readOnly = false,
  targetAndWIPVolume,
  planId,
  date,
  shiftType,
  stationCode,
  getTargetAndWipVolume,
  verifyVolumes,
  resetShiftPlanOutput
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Get override values from Redux store
  const processPathOverrides = useAppSelector((state) => state.volumesAndWip.processPathOverrides);

  // Validate that all overrides have override reasons
  const validateOverrides = (): boolean => {
    const errorMessages: string[] = [];
    
    // Check targetPrimaryVolumeDistribution
    processPathOverrides.volumeContext.secondaryPathVolumeData.targetPrimaryVolumeDistribution.forEach((dist) => {
      if (dist.volume !== undefined && dist.volume !== null && !dist.overrideReason) {
        const processPath = targetAndWIPVolume?.primaryTargetAndWipVolume?.find(
          (v) => v.label === dist.operationName
        );
        const displayName = processPath?.displayName || dist.operationName;
        errorMessages.push(`Expected Volume override for "${displayName}" requires a reason`);
      }
    });
    
    // Check targetSecondaryVolumeDistribution
    processPathOverrides.volumeContext.secondaryPathVolumeData.targetSecondaryVolumeDistribution.forEach((dist) => {
      if (dist.volume !== undefined && dist.volume !== null && !dist.overrideReason) {
        const processPath = targetAndWIPVolume?.secondaryTargetAndWipVolume?.find(
          (v) => v.label === dist.operationName
        );
        const displayName = processPath?.displayName || dist.operationName;
        errorMessages.push(`Expected Volume override for "${displayName}" requires a reason`);
      }
    });
    
    // Check wipSecondaryVolumeDistribution
    processPathOverrides.volumeContext.secondaryPathVolumeData.wipSecondaryVolumeDistribution.forEach((dist) => {
      if (dist.volume !== undefined && dist.volume !== null && !dist.overrideReason) {
        const processPath = targetAndWIPVolume?.secondaryTargetAndWipVolume?.find(
          (v) => v.label === dist.operationName
        );
        const displayName = processPath?.displayName || dist.operationName;
        errorMessages.push(`Buffer override for "${displayName}" requires a reason`);
      }
    });
    
    if (errorMessages.length > 0) {
      const errorMessage = "Please provide override reasons for the following:\n\n" + 
        errorMessages.map((error, index) => `${index + 1}. ${error}`).join("\n");
      dispatch(showNotification({
        title: "Validation Required",
        message: errorMessage,
        type: "warning"
      }));
      return false;
    }
    
    return true;
  };

  // Fetch volumes data on component mount
  useEffect(() => {
    const loadVolumesData = async () => {
        setLoading(true);
        if (planId && date && stationCode && shiftType) {
          console.log(`Calling getShiftPlanOutput from reviewAndPublish page with 
          planId:${planId}, date:${date}, stationCode:${stationCode}, shiftType:${shiftType}`);
          await getTargetAndWipVolume(buildGetTargetAndWIPVolumeRequest(planId, date, stationCode, shiftType));
        }
        setLoading(false);
    };
    loadVolumesData();
  }, [planId, date, stationCode, shiftType]);

  // Handle clear overrides
  const handleClearOverrides = () => {
      dispatch(clearAllProcessPathOverrides());
  };

  // Handle verify - collect all override values and save them
  const handleVerify = async () => {
    // Validate overrides before proceeding
    const isValid = validateOverrides();
    
    if (!isValid) {
      return; // Stop execution - error message already shown
    }
    
    try {
      if (planId) {
        // Pass the override data in the new format to verifyVolumes
        const result = await verifyVolumes(planId, processPathOverrides);
        
        // Access the response from the thunk's payload
        const response = result.payload;

        if (response && response.status === PlanStatus.RECEIVED_USER_INPUT) {
          // Navigate to next step or show confirmation
          console.log("Verify volume successful.")
          dispatch(showNotification({
            title: "Volumes Verified",
            message: "Volumes verified successfully!",
            type: "success"
          }));
          resetShiftPlanOutput();
          navigate(ROUTES.SHIFT_PLAN_REVIEW);
        }
      }
    } catch (error) {
      console.error('Error during verification:', error);
    }
  };

  return (
    <Box spacingInset="300" className="volumes-and-wip">
      {loading ? (
          <RatesRosterLoader status="loading" type={"volumes"} message={"Loading volumes data..."}/>
      ) : targetAndWIPVolume ? (
        <>
          <Box className="header">
            <Box className="title-section">
              <Heading type="h400" level={2}>Volume and Buffer</Heading>
              <Text color="secondary">Verify or update the values below and save.</Text>
            </Box>
            <VolumeTargetDisplay />
          </Box>

          <Box className="illustration">
            <img src={volumeAndWip} alt={"Volume and WIP Illustration"}/>
          </Box>

          {
            targetAndWIPVolume && targetAndWIPVolume.primaryTargetAndWipVolume &&
              [...targetAndWIPVolume.primaryTargetAndWipVolume]
                // Ensure "Primary Grading" process path is always displayed first in the UI.
                // All other process paths retain their original order.
                .sort((a, b) => {
                  return Number(b.displayName === "Primary Grading") - Number(a.displayName === "Primary Grading");
                }).map((volume, index) => (
                  <ProcessPathVolumeRow
                    key={`primary-${volume.label}-${index}`}
                    processPathDisplayName={volume.displayName}
                    processPathName={volume.label}
                    targetVolume={volume.targetVolume}
                    readOnly={readOnly}
                  />
              ))
          }

          {
              targetAndWIPVolume && targetAndWIPVolume.secondaryTargetAndWipVolume &&
              targetAndWIPVolume.secondaryTargetAndWipVolume.map((volume, index) => (
                  <ProcessPathVolumeRow
                      key={`secondary-${volume.label}-${index}`}
                      processPathDisplayName={volume.displayName}
                      processPathName={volume.label}
                      targetVolume={volume.targetVolume}
                      wipVolume={volume.wipVolume}
                      readOnly={readOnly}
                  />
              ))
          }

          {!readOnly && (
            <Footer
              onClearOverrides={handleClearOverrides}
              onNext={handleVerify}
            />
          )}
        </>
      ) : (
          <Box spacingInset="400">
            <Text color="secondary">No volumes data available</Text>
          </Box>
      )}
    </Box>
  );
};

export default VolumesAndWip;

import './index.scss';